import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { map } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import { StatGroupComponent, StatItem } from '../../components/stat-group/stat-group.component';
import { SearchBarComponent } from '../../components/search-bar/search-bar.component';
import { ConfirmDialogComponent } from '../../components/confirm-dialog/confirm-dialog.component';
import {
  FilterDropdownComponent,
  FilterDropdownOption,
} from '../../components/filter-dropdown/filter-dropdown.component';
import { ResetFilterButtonComponent } from '../../components/reset-filter-button/reset-filter-button.component';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { CodeIdChipComponent } from '../../components/code-id-chip/code-id-chip.component';
import { SavedViewOption, SavedViewsMenuComponent } from '../../shared/saved-views/saved-views-menu.component';
import { TestdatenblattService } from '../../services/testdatenblatt.service';
import { SavedViewService } from '../../services/saved-view.service';
import { SavedView, SavedViewContext } from '../../models/saved-view.model';
import {
  buildTestdatenblattSavedViewFiltersJson,
  parseTestdatenblattSavedViewFilters,
} from './testdatenblatt-saved-views.utils';
import { ProjectRoleService } from '../../services/project-role.service';
import { PageSizePreferenceService } from '../../services/page-size-preference.service';
import { Testdatenblatt } from '../../models/testdatenblatt.model';
import { ListFocusScrollDirective } from '../../shared/scroll-container/list-focus-scroll.directive';

interface SheetRow {
  id: string;
  displayId: string;
  name: string;
  description: string;
  variableCount: number;
  rowCount: number;
  moduleCount: number;
  authorName: string | null;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_ROUTE_KEY = 'testdatenblatt';
const SAVED_VIEW_CONTEXT: SavedViewContext = 'TEST_DATA_SHEETS';

@Component({
  selector: 'app-testdatenblatt',
  imports: [
    ListFocusScrollDirective,
    TranslatePipe,
    MatButton,
    MatIcon,
    MatProgressSpinner,
    ProjectNavComponent,
    PageHeaderComponent,
    StatGroupComponent,
    SearchBarComponent,
    ConfirmDialogComponent,
    FilterDropdownComponent,
    ResetFilterButtonComponent,
    AvatarComponent,
    CodeIdChipComponent,
    SavedViewsMenuComponent,
  ],
  templateUrl: './testdatenblatt.component.html',
  styleUrl: './testdatenblatt.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestdatenblattComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly service = inject(TestdatenblattService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly projectRoleService = inject(ProjectRoleService);
  private readonly pageSizePref = inject(PageSizePreferenceService);
  private readonly savedViewService = inject(SavedViewService);

  readonly isProjectReader = this.projectRoleService.isProjectReader;

  private readonly currentLang = toSignal(this.translate.onLangChange.pipe(map((e) => e.lang)), {
    initialValue: this.translate.currentLang,
  });

  readonly projectId: string;

  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly deleteError = signal(false);
  readonly deleting = signal(false);
  readonly searchTerm = signal('');
  // Skalarer Wert: das Design fuehrt `autorF` als Skalar (page-testdatenblatt.jsx:60);
  // ein `string[]` waere Multi-Select-Sematik, die dieser Screen weder anbietet noch
  // persistiert (NEX-1060-Review „Sollte vor Merge").
  readonly selectedAuthor = signal<string>('');

  readonly savedViews = signal<SavedView[]>([]);
  readonly activeSavedViewName = signal<string | null>(null);
  readonly savedViewsError = signal<string | null>(null);

  readonly savedViewsOptions = computed<SavedViewOption[]>(() => {
    this.currentLang();
    return this.savedViews().map((v) => ({ name: v.name, summary: this.summarizeSavedView(v) }));
  });

  readonly showDeleteModal = signal(false);
  readonly rowToDelete = signal<SheetRow | null>(null);

  readonly pageIndex = signal(0);
  readonly pageSize = signal(this.pageSizePref.get(PAGE_SIZE_ROUTE_KEY, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE));
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  private readonly sheets = signal<Testdatenblatt[]>([]);

  readonly rows = computed<SheetRow[]>(() => {
    this.currentLang();
    return this.sheets().map((s) => this.toRow(s));
  });

  readonly statItems = computed<StatItem[]>(() => {
    this.currentLang();
    const all = this.rows();
    const totalRows = all.reduce((sum, r) => sum + r.rowCount, 0);
    return [
      { label: this.translate.instant('testdatenblaetter.stats.total'), value: all.length },
      { label: this.translate.instant('testdatenblaetter.stats.rows'), value: totalRows },
    ];
  });

  readonly statsAriaLabel = computed(() => {
    this.currentLang();
    const all = this.rows();
    return this.translate.instant('testdatenblaetter.stats.ariaLabel', {
      total: all.length,
      rows: all.reduce((sum, r) => sum + r.rowCount, 0),
    });
  });

  readonly authorOptions = computed<FilterDropdownOption[]>(() => toLabelOptions(this.rows().map((r) => r.authorName)));

  readonly hasFilters = computed(() => !!this.searchTerm() || !!this.selectedAuthor());

  readonly filteredRows = computed<SheetRow[]>(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const author = this.selectedAuthor();
    return this.rows().filter((row) => {
      if (
        term &&
        !row.name.toLowerCase().includes(term) &&
        !row.description.toLowerCase().includes(term) &&
        !row.displayId.toLowerCase().includes(term)
      ) {
        return false;
      }
      if (author && row.authorName !== author) return false;
      return true;
    });
  });

  readonly total = computed(() => this.filteredRows().length);
  readonly unfilteredTotal = computed(() => this.rows().length);
  readonly resultCountKey = computed(() =>
    this.unfilteredTotal() === 1 ? 'testdatenblaetter.resultCountOne' : 'testdatenblaetter.resultCountOther',
  );
  readonly hasResults = computed(() => this.total() > 0);
  readonly pageCount = computed(() => Math.max(1, Math.ceil(this.total() / this.pageSize())));
  readonly rangeStart = computed(() => (this.total() === 0 ? 0 : this.pageIndex() * this.pageSize() + 1));
  readonly rangeEnd = computed(() => Math.min(this.total(), (this.pageIndex() + 1) * this.pageSize()));

  readonly pagedRows = computed<SheetRow[]>(() => {
    const start = this.pageIndex() * this.pageSize();
    return this.filteredRows().slice(start, start + this.pageSize());
  });

  constructor() {
    this.projectId = this.route.snapshot.params['projectId'];
  }

  ngOnInit(): void {
    this.loadList();
    this.loadSavedViews();
  }

  onSearchChange(term: string): void {
    this.searchTerm.set(term);
    this.pageIndex.set(0);
    this.clearActiveSavedView();
  }

  onAuthorChange(value: string): void {
    this.selectedAuthor.set(value);
    this.pageIndex.set(0);
    this.clearActiveSavedView();
  }

  onResetFilters(): void {
    this.searchTerm.set('');
    this.selectedAuthor.set('');
    this.pageIndex.set(0);
    this.clearActiveSavedView();
  }

  onSavedViewSelected(name: string): void {
    const view = this.savedViews().find((v) => v.name === name);
    if (!view) return;
    const filters = parseTestdatenblattSavedViewFilters(view.filtersJson);
    this.savedViewsError.set(null);
    this.searchTerm.set(filters.query ?? '');
    this.selectedAuthor.set(filters.author ?? '');
    this.pageIndex.set(0);
    // Marker vor dem Prune setzen: verwirft der Prune den Autor, nimmt er die
    // Markierung gleich wieder weg — sonst behauptet das Menue, die Ansicht sei
    // aktiv, waehrend die Trefferliste eine andere ist (NEX-1060-Review Runde 2).
    this.activeSavedViewName.set(view.name);
    this.pruneStaleAuthor();
  }

  onSavedViewSaved(name: string): void {
    const filtersJson = buildTestdatenblattSavedViewFiltersJson({
      query: this.searchTerm() || null,
      author: this.selectedAuthor() || null,
    });
    this.savedViewService
      .upsert(this.projectId, { name, context: SAVED_VIEW_CONTEXT, filtersJson })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => {
          this.savedViews.update((list) => {
            const rest = list.filter((v) => v.name !== saved.name);
            return [...rest, saved].sort((a, b) => a.name.localeCompare(b.name));
          });
          this.activeSavedViewName.set(saved.name);
          this.savedViewsError.set(null);
        },
        error: () => this.savedViewsError.set(this.translate.instant('testdatenblaetter.savedViews.saveError')),
      });
  }

  onSavedViewDeleted(name: string): void {
    this.savedViewsError.set(null);
    this.savedViews.update((list) => list.filter((v) => v.name !== name));
    if (this.activeSavedViewName() === name) {
      this.activeSavedViewName.set(null);
    }
  }

  onSavedViewDeleteFailed(): void {
    this.savedViewsError.set(this.translate.instant('testdatenblaetter.savedViews.deleteError'));
  }

  onPageChange(index: number): void {
    this.pageIndex.set(index);
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.pageIndex.set(0);
    this.pageSizePref.set(PAGE_SIZE_ROUTE_KEY, size, PAGE_SIZE_OPTIONS);
  }

  onCreate(): void {
    this.router.navigate(['/projects', this.projectId, 'testdatenblaetter', 'neu']);
  }

  onEdit(row: SheetRow): void {
    this.router.navigate(['/projects', this.projectId, 'testdatenblaetter', row.id, 'bearbeiten']);
  }

  onRowKeydown(event: Event, row: SheetRow): void {
    if (event.target !== event.currentTarget) return;
    this.onEdit(row);
  }

  onDeleteRequest(row: SheetRow): void {
    this.rowToDelete.set(row);
    this.showDeleteModal.set(true);
  }

  onCancelDelete(): void {
    this.showDeleteModal.set(false);
    this.rowToDelete.set(null);
  }

  onConfirmDelete(): void {
    const row = this.rowToDelete();
    if (!row) return;
    this.deleteError.set(false);
    this.deleting.set(true);
    this.service
      .delete(this.projectId, row.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.sheets.set(this.sheets().filter((s) => s.id !== row.id));
          // Autor koennte durch das geloeschte Blatt die letzte Option gewesen sein
          // — dann bleibt selectedAuthor sonst auf einem Wert, den das Dropdown nicht
          // mehr anbietet und der Nutzer nicht mehr abwaehlen kann (NEX-1060-Review B1).
          this.pruneStaleAuthor();
          this.deleting.set(false);
          this.showDeleteModal.set(false);
          this.rowToDelete.set(null);
          if (this.pageIndex() >= this.pageCount()) {
            this.pageIndex.set(Math.max(0, this.pageCount() - 1));
          }
        },
        error: () => {
          this.deleting.set(false);
          this.showDeleteModal.set(false);
          this.rowToDelete.set(null);
          this.deleteError.set(true);
        },
      });
  }

  readonly deleteMessage = computed(() => {
    this.currentLang();
    const row = this.rowToDelete();
    if (!row) return '';
    const key =
      row.moduleCount === 0
        ? 'testdatenblaetter.deleteMessageNone'
        : row.moduleCount === 1
          ? 'testdatenblaetter.deleteMessageOne'
          : 'testdatenblaetter.deleteMessageOther';
    return this.translate.instant(key, {
      name: row.name,
      id: row.displayId,
      count: row.moduleCount,
    });
  });

  private loadList(): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.service
      .list(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (sheets) => {
          this.sheets.set(sheets);
          this.pruneStaleAuthor();
          this.loading.set(false);
        },
        error: () => {
          this.sheets.set([]);
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  private loadSavedViews(): void {
    this.savedViewService
      .list(this.projectId, SAVED_VIEW_CONTEXT)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (views) => this.savedViews.set(views),
        error: () => this.savedViews.set([]),
      });
  }

  private summarizeSavedView(view: SavedView): string {
    const filters = parseTestdatenblattSavedViewFilters(view.filtersJson);
    const parts = [filters.query, filters.author].filter((s): s is string => !!s);
    if (parts.length === 0) return this.translate.instant('testdatenblaetter.savedViews.summaryAll');
    return parts.join(' · ');
  }

  private clearActiveSavedView(): void {
    if (this.activeSavedViewName() !== null) this.activeSavedViewName.set(null);
  }

  private pruneStaleAuthor(): void {
    const current = this.selectedAuthor();
    if (!current) return;
    // Ohne geladene Liste ist jeder Autor "unbekannt": waehrend des Ladens und nach
    // einem Ladefehler darf das keinen Filter verwerfen (NEX-1060-Review Runde 1/2).
    if (this.sheets().length === 0) return;
    const available = new Set(this.authorOptions().map((o) => o.value));
    if (available.has(current)) return;
    this.selectedAuthor.set('');
    this.clearActiveSavedView();
  }

  private toRow(sheet: Testdatenblatt): SheetRow {
    const name = (sheet.name ?? '').trim();
    return {
      id: sheet.id,
      displayId: sheet.key,
      name: name || this.translate.instant('testdatenblaetter.noName'),
      description: (sheet.beschreibung ?? '').trim(),
      variableCount: sheet.spalten.length,
      rowCount: sheet.zeilen.length,
      moduleCount: sheet.testModuleIds.length,
      authorName: sheet.assigneeName?.trim() || null,
    };
  }
}

// Freitext-Werte (Autor) zu eindeutigen, alphabetisch sortierten Filter-Optionen
// verdichten; Label entspricht dem Wert. null wird verworfen.
function toLabelOptions(values: (string | null)[]): FilterDropdownOption[] {
  const distinct = [...new Set(values.filter((v): v is string => v !== null))].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }),
  );
  return distinct.map((value) => ({ value, label: value }));
}
